import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, Lock, Flame, Crown } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';
import InfoTooltip from 'components/_UI/InfoTooltip';
import SalesBar from './SalesBar';
import {
    tone,
    toneForPercent,
    statusChip,
    statusLabel,
    statusForBatches,
    statusForBatch,
    summarizeTicket,
    summarizeBatch,
    percentOf,
    displayPercent,
    formatInt,
    isSellingOut,
} from './salesTokens';

/* A retenção não é só a taxa de serviço: juros de parcelamento entram no mesmo
   campo, e em evento com contrato próprio o percentual é o negociado. Rotular
   como "taxa da plataforma" faria o produtor recalcular 10% e achar erro. */
const FEES_HINT = 'Taxa de serviço e juros de parcelamento retidos no pagamento';

function formatPeriod(start, end) {
    if (!start && !end) return null;

    const fmt = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    };

    const from = start && fmt(start);
    const to = end && fmt(end);

    if (from && to) return `${from} → ${to}`;
    if (from) return `A partir de ${from}`;
    return `Até ${to}`;
}

/**
 * Card de desempenho de um item à venda — serve para ingressos e produtos, que
 * têm a mesma forma no payload (name, price, image, hidden_code, batches[]).
 *
 * A faixa lateral colorida carrega o desempenho: dá para varrer a lista inteira
 * e ver quem vende e quem está parado sem ler um número sequer.
 */
export default function ItemPerformanceCard({ item, hiddenLabel = 'Item oculto', isTopSeller = false }) {
    const [expanded, setExpanded] = useState(false);

    const summary = useMemo(() => summarizeTicket(item), [item]);
    const percent = percentOf(summary.sold, summary.total);
    const t = tone[toneForPercent(percent)];

    const status = statusForBatches(item?.batches);
    const chip = statusChip[status];

    const batches = item?.batches || [];
    const remaining = Math.max(summary.estimated - summary.collected, 0);
    const sellingOut = isSellingOut(percent);

    // Item sem desconto nenhum não ganha coluna: a maioria dos eventos não usa
    // cupom, e uma coluna zerada em toda a lista esconde as que têm valor.
    const hasDiscount = summary.knowsChain && summary.discount > 0;
    const discountShare = percentOf(summary.discount, summary.gross);

    const panelId = `lotes-item-${item?.id}`;

    return (
        <Card $tone={t}>
            <Accent $tone={t} aria-hidden='true' />

            <Body>
                <Head>
                    {item?.image && <Thumb src={item.image} alt='' loading='lazy' />}

                    <HeadMain>
                        <NameRow>
                            {item?.hidden_code && (
                                <InfoTooltip
                                    bgColor={colors.night}
                                    text={`${hiddenLabel} · Código: ${item.hidden_code}`}
                                    type='none'
                                >
                                    <LockBadge aria-label={`${hiddenLabel}, código ${item.hidden_code}`}>
                                        <Lock size={11} strokeWidth={2.5} aria-hidden='true' />
                                    </LockBadge>
                                </InfoTooltip>
                            )}
                            <Name title={item?.name}>{item?.name}</Name>
                            <StatusChip $chip={chip}>{statusLabel[status]}</StatusChip>

                            {isTopSeller && (
                                <TopSellerBadge title='Maior arrecadação entre os itens'>
                                    <Crown size={11} strokeWidth={2.5} aria-hidden='true' />
                                    Campeão
                                </TopSellerBadge>
                            )}

                            {/* Oportunidade, não erro: hora de abrir novo lote. */}
                            {sellingOut && (
                                <SellingOutBadge title='Restam menos de 10% — considere abrir um novo lote'>
                                    <Flame size={11} strokeWidth={2.5} aria-hidden='true' />
                                    Esgotando
                                </SellingOutBadge>
                            )}
                        </NameRow>

                        <Price>
                            {currency.format(Number(item?.price) || 0)}
                            <PriceHint> · preço cheio</PriceHint>
                        </Price>
                    </HeadMain>

                    <SoldBadge $tone={t}>
                        <SoldPercent>{displayPercent(percent)}%</SoldPercent>
                        <SoldCaption>vendido</SoldCaption>
                    </SoldBadge>
                </Head>

                <SalesBar
                    percent={percent}
                    tone={t}
                    label={`${item?.name}: ${formatInt(summary.sold)} de ${formatInt(summary.total)} vendidos`}
                />

                <BarCaption>
                    <strong>{formatInt(summary.sold)}</strong> de {formatInt(summary.total)} vendidos
                    <Dot aria-hidden='true'>·</Dot>
                    {formatInt(summary.available)} disponíveis
                    {/* Reserva é venda que ainda pode não acontecer: fica separada
                        do arrecadado para o produtor não contar dinheiro que não entrou. */}
                    {summary.reserved > 0 && (
                        <>
                            <Dot aria-hidden='true'>·</Dot>
                            <PendingCount>{formatInt(summary.reserved)} aguardando pagamento</PendingCount>
                        </>
                    )}
                </BarCaption>

                {/* "Potencial total" continua fora: seria a soma das outras duas
                    colunas e vira ruído. Desconto entra porque é o oposto disso —
                    não sai de nenhuma conta entre os outros valores da tela. */}
                <Figures>
                    <Figure>
                        <FigureLabel>Arrecadado</FigureLabel>
                        {/* Verde = dinheiro que entrou de fato; o restante fica
                            neutro porque ainda é projeção. */}
                        <CollectedValue $earned={summary.collected > 0}>
                            {currency.format(summary.collected)}
                        </CollectedValue>
                        <FigureHint>sem taxas</FigureHint>
                    </Figure>

                    {hasDiscount && (
                        <>
                            <FigureDivider aria-hidden='true' />

                            <Figure>
                                <FigureLabel>Desconto concedido</FigureLabel>
                                {/* Neutro de propósito: nesta tela cor significa
                                    desempenho de venda, e desconto não é bom nem
                                    ruim — é uma decisão do produtor. */}
                                <FigureValue title='Cupons e descontos aplicados nas vendas deste item'>
                                    −{currency.format(summary.discount)}
                                </FigureValue>
                                <FigureHint>
                                    {displayPercent(discountShare)}% de {currency.format(summary.gross)} em vendas
                                </FigureHint>
                            </Figure>
                        </>
                    )}

                    <FigureDivider aria-hidden='true' />

                    <Figure>
                        <FigureLabel>A realizar</FigureLabel>
                        <FigureValue>{currency.format(remaining)}</FigureValue>
                        <FigureHint>
                            {summary.collected > 0
                                ? `de ${currency.format(summary.estimated)} potenciais`
                                : 'nenhuma venda ainda'}
                        </FigureHint>
                    </Figure>
                </Figures>

                {batches.length > 0 && (
                    <>
                        <BatchToggle
                            type='button'
                            onClick={() => setExpanded((prev) => !prev)}
                            aria-expanded={expanded}
                            aria-controls={panelId}
                        >
                            <span>
                                {batches.length} {batches.length === 1 ? 'lote' : 'lotes'}
                            </span>
                            <Chevron $open={expanded} aria-hidden='true'>
                                <ChevronDown size={14} strokeWidth={2.5} />
                            </Chevron>
                        </BatchToggle>

                        {expanded && (
                            <BatchList id={panelId}>
                                {batches.map((batch) => {
                                    // Mesma leitura do resumo do item: lote excluído conta só o
                                    // que saiu, senão a linha diria "10 de 10 vendidos" onde o
                                    // produtor tirou 7 unidades da venda.
                                    const money = summarizeBatch(batch);
                                    const { out: sold, total: quantity, reserved } = money;
                                    const batchPercent = percentOf(sold, quantity);
                                    const batchTone = tone[toneForPercent(batchPercent)];
                                    const batchChip = statusChip[statusForBatch(batch)];
                                    const period = formatPeriod(batch?.sale_start, batch?.sale_end);

                                    // Lote sem venda nenhuma não tem cadeia para mostrar —
                                    // cinco zeros em linha só ocupariam a altura da lista.
                                    const showChain = money.knowsChain && money.gross > 0;

                                    return (
                                        <BatchRow key={batch.id} $tone={batchTone}>
                                            <BatchTop>
                                                <BatchMain>
                                                    <BatchName title={batch?.name}>{batch?.name}</BatchName>
                                                    <BatchMeta>
                                                        {currency.format(Number(batch?.price) || 0)}
                                                        <Dot aria-hidden='true'>·</Dot>
                                                        {formatInt(sold)} de {formatInt(quantity)} vendidos
                                                        {reserved > 0 && (
                                                            <>
                                                                <Dot aria-hidden='true'>·</Dot>
                                                                {formatInt(reserved)} aguardando pagamento
                                                            </>
                                                        )}
                                                        {period && (
                                                            <>
                                                                <Dot aria-hidden='true'>·</Dot>
                                                                {period}
                                                            </>
                                                        )}
                                                    </BatchMeta>
                                                </BatchMain>

                                                <BatchBarWrap>
                                                    <SalesBar
                                                        percent={batchPercent}
                                                        tone={batchTone}
                                                        height={6}
                                                        label={`${batch?.name}: ${formatInt(sold)} de ${formatInt(quantity)} vendidos`}
                                                    />
                                                </BatchBarWrap>

                                                <BatchRight>
                                                    <BatchCollected $earned={money.collected > 0}>
                                                        {currency.format(money.collected)}
                                                    </BatchCollected>
                                                    <StatusChip $chip={batchChip} $small>
                                                        {statusLabel[statusForBatch(batch)]}
                                                    </StatusChip>
                                                </BatchRight>
                                            </BatchTop>

                                            {/* A conferência do produtor, na ordem em que ele
                                                faz a conta: quanto vendeu, quanto abateu, quanto
                                                o cliente pagou, quanto foi retido, quanto sobrou. */}
                                            {showChain && (
                                                <BatchChain>
                                                    <ChainCell>
                                                        <ChainLabel>Bruto</ChainLabel>
                                                        <ChainValue>{currency.format(money.gross)}</ChainValue>
                                                    </ChainCell>

                                                    <ChainCell>
                                                        <ChainLabel>Desconto</ChainLabel>
                                                        <ChainValue $muted={money.discount === 0}>
                                                            {money.discount > 0 ? (
                                                                <>
                                                                    −{currency.format(money.discount)}
                                                                    {/* O percentual é o que o produtor
                                                                        compara entre lotes; o valor
                                                                        sozinho não diz se pesou. */}
                                                                    <ChainShare>
                                                                        {' '}
                                                                        ·{' '}
                                                                        {displayPercent(
                                                                            percentOf(money.discount, money.gross)
                                                                        )}
                                                                        %
                                                                    </ChainShare>
                                                                </>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </ChainValue>
                                                    </ChainCell>

                                                    <ChainCell>
                                                        <ChainLabel>Pago pelo cliente</ChainLabel>
                                                        <ChainValue>{currency.format(money.charged)}</ChainValue>
                                                    </ChainCell>

                                                    <ChainCell>
                                                        <ChainLabel title={FEES_HINT}>Taxas e juros</ChainLabel>
                                                        <ChainValue $muted={money.fees === 0} title={FEES_HINT}>
                                                            {money.fees > 0 ? `−${currency.format(money.fees)}` : '—'}
                                                        </ChainValue>
                                                    </ChainCell>

                                                    <ChainCell>
                                                        <ChainLabel>Líquido</ChainLabel>
                                                        <ChainValue $strong>{currency.format(money.collected)}</ChainValue>
                                                    </ChainCell>
                                                </BatchChain>
                                            )}
                                        </BatchRow>
                                    );
                                })}
                            </BatchList>
                        )}
                    </>
                )}
            </Body>
        </Card>
    );
}

/* ─────────────────────────────  Shell  ───────────────────────────── */

const Card = styled.article`
    display: flex;
    align-items: stretch;
    border-radius: 14px;
    border: 1px solid ${({ $tone }) => $tone.border};
    background: ${colors.neutralLight};
    overflow: hidden;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 18px ${({ $tone }) => $tone.accent}22;
        border-color: ${({ $tone }) => $tone.accent};
    }
`;

/* A faixa de desempenho: o sinal que permite ler a lista de relance. */
const Accent = styled.div`
    width: 4px;
    flex-shrink: 0;
    background: ${({ $tone }) => $tone.accent};
`;

const Body = styled.div`
    flex: 1;
    min-width: 0;
    padding: 16px;
    background: ${colors.neutralLight};
`;

const Head = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
`;

/* Só renderiza quando o item tem imagem (produtos costumam ter, ingressos não). */
const Thumb = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 9px;
    object-fit: cover;
    flex-shrink: 0;
    background: ${colors.neutralBackground};
`;

const HeadMain = styled.div`
    min-width: 0;
    flex: 1;
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
    margin-bottom: 4px;
`;

const Name = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

const LockBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background: ${colors.secondaryPurple}15;
    color: #6d14cc;
    flex-shrink: 0;
    cursor: help;
`;

const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    height: ${({ $small }) => ($small ? '18px' : '20px')};
    padding: 0 8px;
    border-radius: 999px;
    font-size: ${({ $small }) => ($small ? '10px' : '11px')};
    font-weight: 700;
    letter-spacing: 0.2px;
    background: ${({ $chip }) => $chip.bg};
    color: ${({ $chip }) => $chip.text};
    border: 1px solid ${({ $chip }) => $chip.border};
`;

const TopSellerBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: #fdf6e3;
    color: #7a5300;
    border: 1px solid #edd9a3;
    font-size: 11px;
    font-weight: 700;
    cursor: help;
`;

const SellingOutBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: #fff1e8;
    color: #9a3b0b;
    border: 1px solid #f6cdb2;
    font-size: 11px;
    font-weight: 700;
    cursor: help;
`;

const Price = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const PriceHint = styled.span`
    font-weight: 400;
    color: ${colors.night};
`;

const SoldBadge = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    padding: 6px 11px;
    border-radius: 10px;
    background: ${({ $tone }) => $tone.soft};
    border: 1px solid ${({ $tone }) => $tone.border};
    color: ${({ $tone }) => $tone.text};
`;

const SoldPercent = styled.span`
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.5px;
    color: inherit;
`;

const SoldCaption = styled.span`
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    opacity: 0.85;
`;

const BarCaption = styled.p`
    margin: 8px 0 0;
    font-size: 12px;
    color: ${colors.night};

    strong {
        color: ${colors.nightDark};
        font-weight: 700;
    }
`;

/* Âmbar do tom "devagar": pendência é aviso, não erro nem sucesso. */
const PendingCount = styled.span`
    color: ${tone.slow.text};
    font-weight: 600;
`;

const Dot = styled.span`
    margin: 0 6px;
    color: ${colors.nightLight};
`;

/* ─────────────────────────────  Valores  ───────────────────────────── */

const Figures = styled.div`
    display: flex;
    align-items: stretch;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(16, 24, 40, 0.09);
`;

const Figure = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FigureDivider = styled.div`
    width: 1px;
    background: rgba(16, 24, 40, 0.12);
    margin: 0 20px;
    flex-shrink: 0;
`;

const FigureLabel = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${colors.night};
`;

const FigureValue = styled.span`
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: ${colors.night};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CollectedValue = styled.span`
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ $earned }) => ($earned ? '#157F3C' : colors.nightDark)};
`;

const FigureHint = styled.span`
    font-size: 10px;
    color: ${colors.night};
`;

/* ─────────────────────────────  Lotes  ───────────────────────────── */

const BatchToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(16, 24, 40, 0.12);
    background: transparent;
    color: ${colors.nightDark};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: ${colors.neutralBackground};
        border-color: rgba(16, 24, 40, 0.2);
    }

    &:focus-visible {
        outline: 2px solid ${colors.secondaryPurple};
        outline-offset: 2px;
    }
`;

const Chevron = styled.span`
    display: inline-flex;
    color: ${colors.night};
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const BatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 10px;
`;

const BatchRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${({ $tone }) => $tone.soft};
    border: 1px solid ${({ $tone }) => $tone.border};
`;

/* A linha de sempre: nome, barra e líquido. A cadeia de valores vem abaixo,
   ocupando a largura toda — por isso a responsividade desceu para cá. */
const BatchTop = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: center;
        gap: 14px;
    }
`;

const BatchMain = styled.div`
    min-width: 0;

    @media screen and (min-width: ${breakpoints.smd}) {
        width: 40%;
    }
`;

const BatchName = styled.p`
    margin: 0 0 2px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const BatchMeta = styled.p`
    margin: 0;
    font-size: 11px;
    color: ${colors.night};
`;

const BatchBarWrap = styled.div`
    flex: 1;
    min-width: 0;
`;

const BatchRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.smd}) {
        justify-content: flex-end;
        width: 190px;
    }
`;

const BatchCollected = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${({ $earned }) => ($earned ? '#157F3C' : colors.nightDark)};
`;

/* ── Cadeia de valores do lote ──
   Grade em vez de linha corrida: os cinco valores precisam alinhar entre um
   lote e outro para a coluna de desconto ser comparável de relance. */
const BatchChain = styled.dl`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 14px;
    margin: 0;
    padding-top: 9px;
    border-top: 1px dashed rgba(16, 24, 40, 0.14);

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }
`;

const ChainCell = styled.div`
    min-width: 0;
`;

const ChainLabel = styled.dt`
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.night};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ChainShare = styled.span`
    font-weight: 600;
    color: ${colors.night};
`;

const ChainValue = styled.dd`
    margin: 2px 0 0;
    font-size: 12px;
    font-weight: ${({ $strong }) => ($strong ? 700 : 600)};
    color: ${({ $muted, $strong }) => {
        if ($muted) return colors.nightLight;
        return $strong ? '#157F3C' : colors.nightDark;
    }};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
